import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as AnnouncementStore from './announcement.state';
import * as Adapters from './announcement.adapters';
import {
  DepartmentVM,
  RecipientVM,
  isHotel,
  isDepartment,
  isManager,
  HotelVM,
  ManagerVM,
  Recipient,
  ALL_EMPLOYEES,
} from '../../../interfaces';
import { Announcements } from '@app/core/async-services/http/versioned/interfaces';
import { API } from '@app/core/models';
import * as fileUtils from '@app/core/utils/file.util';

export const selectFeature =
  createFeatureSelector<AnnouncementStore.State>('announcementState');

export const selectAll = createSelector(selectFeature, (state) => state);

export const selectApiState = createSelector(
  selectFeature,
  (state) => state.api,
);

export const selectGetAnnouncementLoadingState = createSelector(
  selectApiState,
  (state) => state.getAnnouncement,
);

export const selectActiveChannelMessage = createSelector(
  selectFeature,
  (state) => state.message,
);

export const selectAnnouncementDraft = createSelector(
  selectFeature,
  (state) => state.announcementDraft,
);

export const selectActiveChannelName = createSelector(
  selectFeature,
  (state) => state.channel,
);

export const selectViewAnnouncementState = createSelector(
  selectGetAnnouncementLoadingState,
  selectActiveChannelMessage,
  selectActiveChannelName,
  (loadingState, message, channelName) => ({
    isLoading: loadingState !== API.LoadingState.LOADED,
    message,
    channelName,
    documents:
      message && message.attachments
        ? message.attachments.filter(fileUtils.isDocument)
        : [],
  }),
);

const departmentSelectors = Adapters.departments.getSelectors();
const hotelSelectors = Adapters.hotels.getSelectors();
const managerSelectors = Adapters.managers.getSelectors();

export const selectHotelLoadingState = createSelector(
  selectApiState,
  (state) => state.getHotels,
);

export const selectManagersLoadingState = createSelector(
  selectApiState,
  (state) => state.getManagers,
);

export const selectHotelState = createSelector(
  selectFeature,
  (state) => state.hotels,
);

export const selectHotelAll = createSelector(
  selectHotelState,
  hotelSelectors.selectAll,
);

export const selectHotelEntities = createSelector(
  selectHotelState,
  hotelSelectors.selectEntities,
);

export const selectHotelIds = createSelector(
  selectHotelState,
  hotelSelectors.selectIds,
);

export const selectHotelTotal = createSelector(
  selectHotelState,
  hotelSelectors.selectTotal,
);

export const selectDepartmentState = createSelector(
  selectFeature,
  (state) => state.departments,
);

export const selectDepartmentAll = createSelector(
  selectDepartmentState,
  departmentSelectors.selectAll,
);

export const selectDepartmentEntities = createSelector(
  selectDepartmentState,
  departmentSelectors.selectEntities,
);

export const selectDepartmentIds = createSelector(
  selectDepartmentState,
  departmentSelectors.selectIds,
);

export const selectDepartmentTotal = createSelector(
  selectDepartmentState,
  departmentSelectors.selectTotal,
);

export const selectHotelManagerState = createSelector(
  selectFeature,
  (state) => state.hotelManagers,
);

export const selectHotelManagerAll = createSelector(
  selectHotelManagerState,
  managerSelectors.selectAll,
);

export const selectHotelManagerEntities = createSelector(
  selectHotelManagerState,
  managerSelectors.selectEntities,
);

export const selectHotelManagerIds = createSelector(
  selectHotelManagerState,
  managerSelectors.selectIds,
);

export const selectHotelManagerTotal = createSelector(
  selectHotelManagerState,
  managerSelectors.selectTotal,
);

export const selectCorporateManagerState = createSelector(
  selectFeature,
  (state) => state.corporateManagers,
);

export const selectCorporateManagerAll = createSelector(
  selectCorporateManagerState,
  managerSelectors.selectAll,
);

export const selectCorporateManagerEntities = createSelector(
  selectCorporateManagerState,
  managerSelectors.selectEntities,
);

export const selectCorporateManagerIds = createSelector(
  selectCorporateManagerState,
  managerSelectors.selectIds,
);

export const selectCorporateManagerTotal = createSelector(
  selectCorporateManagerState,
  managerSelectors.selectTotal,
);

export const selectRecipients = createSelector(
  selectFeature,
  (state) => state.recipients,
);

export const selectRecipientTotal = createSelector(
  selectRecipients,
  (recipients) => recipients.length,
);

export const selectRecipientsVM = createSelector(
  selectRecipients,
  selectHotelEntities,
  (recipients, hotels) =>
    recipients.map<RecipientVM | undefined>((recipient) => {
      if (isHotel(recipient)) {
        return {
          id: recipient.entID,
          label: `All Employees @ ${recipient.entName}`,
        };
      } else if (isDepartment(recipient)) {
        return {
          id: recipient.departName,
          label: `${recipient.departName} @ ${
            hotels[recipient.entID]?.entName
          }`,
        };
      } else if (isManager(recipient)) {
        return {
          id: recipient.webUserId,
          label: `${recipient.firstName} ${recipient.lastName}`,
        };
      } else if (recipient === ALL_EMPLOYEES) {
        return {
          id: ALL_EMPLOYEES,
          label: `All Employees @ All Hotels`,
        };
      }
    }),
);

export const selectRecipientIds = createSelector(
  selectRecipients,
  (recipients) => {
    const body: Announcements.RecipientIds = {
      entIds: [],
      deptIds: [],
      webUserIds: [],
    };
    if (recipients.includes(ALL_EMPLOYEES)) {
      body.entIds.push(Announcements.CreateRequestKeywords.AllHotels);
      body.deptIds.push(Announcements.CreateRequestKeywords.AllDepartments);
      recipients.forEach((recipient) => {
        if (isManager(recipient)) {
          body.webUserIds.push(recipient.webUserId);
        }
      });
    } else {
      recipients.forEach((recipient) => {
        if (isHotel(recipient)) {
          body.entIds.push(recipient.entID);
        } else if (isDepartment(recipient)) {
          body.deptIds.push(recipient.departmentID);
        } else if (isManager(recipient)) {
          body.webUserIds.push(recipient.webUserId);
        }
      });
    }
    return body;
  },
);

export const selectHotelsVM = createSelector(
  selectHotelAll,
  selectRecipients,
  (hotels, recipients) => {
    const hotelRecipientIds = recipients
      .filter(isHotel)
      .map(({ entID }) => entID);
    return hotels
      .filter((hotel) => !hotelRecipientIds.includes(hotel.entID))
      .map<HotelVM>((hotel) => ({
        id: hotel.entID,
        name: hotel.entName,
        activatedHotelEmployees: hotel.activatedHotelEmployees,
        totalHotelEmployees: hotel.totalHotelEmployees,
      }));
  },
);

export const selectDepartmentsVM = createSelector(
  selectDepartmentAll,
  selectHotelEntities,
  selectRecipients,
  (departments, hotelEntities, recipients) => {
    const departmentRecipientIds = recipients
      .filter(isDepartment)
      .map(({ departmentID }) => departmentID);
    return departments
      .filter(
        (department) =>
          !departmentRecipientIds.includes(department.departmentID),
      )
      .map<DepartmentVM>((department) => ({
        id: department.departmentID,
        name: department.departName,
        hotelName: hotelEntities[department.entID]!.entName,
        activatedEmployees: department.activatedEmployees,
        totalEmployees: department.totalEmployees,
        entId: department.entID,
      }))
      .sort((a, b) =>
        a.name > b.name
          ? 1
          : a.name === b.name
          ? a.hotelName > b.hotelName
            ? 1
            : -1
          : -1,
      );
  },
);

function getNonRecipientManagers(
  managers: Announcements.Manager[],
  recipients: Recipient[],
) {
  const managerRecipientIds = recipients
    .filter(isManager)
    .map(({ webUserId }) => webUserId);
  return managers
    .filter((manager) => !managerRecipientIds.includes(manager.webUserId))
    .map<ManagerVM>((manager) => ({
      id: manager.webUserId,
      firstName: manager.firstName,
      lastName: manager.lastName,
      title: manager.title,
      hotelName: manager.hotel,
      isActivated: manager.userStatus === 'activated',
    }));
}

export const selectHotelManagersVM = createSelector(
  selectHotelManagerAll,
  selectRecipients,
  getNonRecipientManagers,
);

export const selectCorporateManagersVM = createSelector(
  selectCorporateManagerAll,
  selectRecipients,
  getNonRecipientManagers,
);
